import { useState } from 'react';

import { faPhone } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCircleQuestion,
  faComments,
  faCopy,
  faEnvelope,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@repo/videa-components/src';
import { useLocation } from 'react-router-dom';

import { parsePathname } from '../../app/appHeader/path-utils';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';
import {
  useCheckIfAspenPractice,
  useCheckIfDentalCorpPractice,
  useCheckIfHeartlandPractice,
  useCheckIfSagePractice,
} from '../../utils/user';

import { HubSpotFormModal } from './HubSpotForm';

export const CustomerSupport = () => {
  const { trackEvent } = useTrackEvent();
  const isHeartland = useCheckIfHeartlandPractice();
  const isSage = useCheckIfSagePractice();
  const isDentalCorp = useCheckIfDentalCorpPractice();
  const isAspen = useCheckIfAspenPractice();
  const { pathname } = useLocation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  let email = 'support@videa.ai';
  let phoneNumber = '+1 (508) 684-5644';

  if (isHeartland) {
    email = 'itsupport@heartland.com';
    phoneNumber = '(217) 540-6090';
  }
  if (isSage) {
    email = 'http://support.mysagedental.com';
    phoneNumber = '(561) 998-0544';
  }
  if (isDentalCorp) {
    email = 'it.support@dentalcorp.ca';
    phoneNumber = '1-833-511-DENT (3368)';
  }
  if (isAspen) {
    phoneNumber = '1-800-996-6470';
  }

  const handleCopyText = (
    copiedText: string,
    copiedTextType: 'PHONE_NUMBER' | 'EMAIL',
  ) => {
    navigator.clipboard.writeText(copiedText);
    trackEvent(mixpanelEvents.customerSupportInformationCopy, {
      copiedValue: copiedTextType,
    });
  };

  const handleWidgetClick = () => {
    const { currentPage, patientId, imageId } = parsePathname(pathname);
    trackEvent(mixpanelEvents.customerSupportWidgetOpen, {
      currentPage: currentPage,
      patientId: patientId,
      imageId: imageId,
    });
  };

  const handleContactFormClick = () => {
    setIsHubSpotModalOpen(true);
    setIsPopoverOpen(false);
  };

  // Only show HubSpot form for practices without their own support team
  const showHubSpotForm = !isHeartland && !isSage && !isDentalCorp && !isAspen;

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger
          asChild
          className="vai-bg-transparent vai-border-none vai-p-0"
          data-testid="cs-widget"
        >
          <Button size="icon" variant="icon" onClick={handleWidgetClick}>
            <FontAwesomeIcon icon={faCircleQuestion} color="white" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="vai-typography-small vai-px-0 vai-py-1 vai-z-[9999]">
          <div className="vai-text-black">
            <p className="vai-p-2" data-testid="cs-widget-text">
              {isAspen ? (
                <Trans>
                  <b>Who do I contact for assistance?</b> <br /> If your team is
                  experiencing a disruption, please contact TAG Support
                </Trans>
              ) : (
                <Trans>
                  We can help with getting set up, questions about the findings
                  or interface, or whatever support you need.
                </Trans>
              )}
            </p>
            <Separator className="vai-my-1" />
            {isAspen && (
              <>
                <a
                  href="https://aspendent.service-now.com/esc?id=sc_cat_item&sys_id=5fbefda01b651c104f10773bdc4bcbbd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vai-flex vai-items-center vai-gap-2 vai-h-10 vai-p-3 vai-cursor-pointer hover:vai-bg-gray-50 vai-w-full vai-text-left vai-bg-transparent vai-border-none vai-no-underline vai-text-black"
                  data-testid="cs-widget-aspen-portal"
                >
                  <FontAwesomeIcon icon={faComments} />
                  <span>
                    <Trans>Submit a ticket through the Service Portal</Trans>
                  </span>
                </a>
                <Separator className="vai-my-1" />
              </>
            )}
            {showHubSpotForm && (
              <>
                <button
                  className="vai-flex vai-items-center vai-gap-2 vai-h-10 vai-p-3 vai-cursor-pointer hover:vai-bg-gray-50 vai-w-full vai-text-left vai-bg-transparent vai-border-none"
                  data-testid="cs-widget-contact-form"
                  onClick={handleContactFormClick}
                >
                  <FontAwesomeIcon icon={faComments} />
                  <span>
                    <Trans>File a Support Ticket</Trans>
                  </span>
                </button>
                <Separator className="vai-my-1" />
              </>
            )}
            <p
              className="vai-flex vai-items-center vai-gap-2 vai-h-10 vai-p-3"
              data-testid="cs-widget-phone"
            >
              <FontAwesomeIcon icon={faPhone} />
              <span>{phoneNumber}</span>
              <Button
                size="icon"
                variant="ghostIcon"
                onClick={() => handleCopyText(phoneNumber, 'PHONE_NUMBER')}
                className="vai-ml-auto"
              >
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </p>
            {!isAspen && (
              <p
                className="vai-flex vai-items-center vai-gap-2 vai-h-10 vai-p-3"
                data-testid="cs-widget-email"
              >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>{email}</span>
                <Button
                  size="icon"
                  variant="ghostIcon"
                  onClick={() => handleCopyText(email, 'EMAIL')}
                  className="vai-ml-auto"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* HubSpot Modal rendered outside of popover */}
      <HubSpotFormModal
        open={isHubSpotModalOpen}
        onOpenChange={setIsHubSpotModalOpen}
      />
    </>
  );
};
